import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stack,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GavelIcon from "@mui/icons-material/Gavel";
import ElectricBoltSharpIcon from '@mui/icons-material/ElectricBoltSharp';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  createAutoBidRequestAPI,
  createBidApplication,
  deleteAutoBidRequestApi,
  getAuctionDetail,
  placeBidAPi,
  updateAutoBidRequestApi,
} from '../../apis';
import { APIResponse, Auction, FreeKeyObject, TypeSnackbarPayload } from '../../utilities/types';
import { showSnackbar } from '../../store/slices/common.slice';
import {
  getBrowserNetworkInfo,
  getFormattedDate,
  getFormattedEmptyAmountWithUnit,
  getFormattedPrice,
  getUTCTimeStamp,
  isAuctionFuture,
  isAuctionLive,
  parseIntoNumber,
} from '../../utilities/helper';
import { RootState } from '../../store';
import AuthFormModal from '../modals/auth-form.modal';
import CustomAlert from '../elements/custom-alert.element';
import CustomSwal from '../elements/custom-swal.element';
import useAuctionWsHook from '../../hooks/auctionWs.hook';
import HorizontalTabs from '../elements/horizontal-tabs.element';
import CountdownWidget from './countdown-widget';
import AuctionBidGridWidget from './my-account/auction-bid-grid.widget';

interface BidWidgetProps {
  auction_id: number;
  allowPlaceBid?: boolean;
  viewType?: 'seller' | 'admin';
}

// ---------- Component ----------
const BidWidget: React.FC<BidWidgetProps> = ({ auction_id, allowPlaceBid = true, viewType = 'admin' }) => {
  const dispatch = useDispatch();

  const { isAuthenticated, user, token } = useSelector((state: RootState) => state.auth);
  const [showAuthFormModal, setShowAuthFormModal] = useState<boolean>(false);
  const [isPlacingBid, setIsPlacingBid] = useState<boolean>(false);
  const [disablePlaceBidBtn, setDisablePlaceBidBtn] = useState<boolean>(false);
  const [showAuctionCloseMessage, setShowAuctionCloseMessage] = useState<boolean>(false);

  // Tabs
  const [tab, setTab] = useState<string | number>(0);
  const [auction, setAuction] = useState<Auction>();
  // Bid input
  const [bidValue, setBidValue] = useState<string>('');
  const [autoBidIncrementValue, setAutoBidIncrementValue] = useState<string>('');
  const [maxAutoBidAmount, setMaxAutoBidAmount] = useState<string>('');
  const [disableAutoBidSubmit, setDisableAutoBidSubmit] = useState<boolean>(true);
  const [isAutoBidRemoving, setIsAutoBidRemoving] = useState<boolean>(false);

  // Responsive grid sizes: left = 8/12 on md+, right = 4/12

  const getNextBidAmount = (data: Auction | undefined): number => {
    return parseIntoNumber(data?.current_price) + parseIntoNumber(data?.increment_price);
  };

  const fetchAuctionDetail = useCallback(async () => {
    try {
      const payload = { auction_id, user_id: isAuthenticated ? user?.id : null };
      const response = (await getAuctionDetail(payload)) as APIResponse<Auction>;
      if (response.status) {
        const auctionResponse = response.data as Auction;
        const now = getUTCTimeStamp();
        const auctionEndTime = getUTCTimeStamp(auctionResponse.end_time);
        if (now.isAfter(auctionEndTime)) {
          setShowAuctionCloseMessage(true);
        } else {
          setAuction(auctionResponse);
          setBidValue(getNextBidAmount(auctionResponse).toString());
          const auto_bid_detail = auctionResponse?.auto_bid_detail || null;
          if (auto_bid_detail) {
            setAutoBidIncrementValue(
              (auto_bid_detail.increment || auto_bid_detail.increment_price || '').toString()
            );
            setMaxAutoBidAmount((auto_bid_detail.max_amount || '').toString());
          } else {
            setAutoBidIncrementValue(auctionResponse.increment_price.toString());
          }
        }
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [auction_id, dispatch, isAuthenticated, user]);

  const handlePlaceBid = async () => {
    const result = await CustomSwal.fire({
      title: 'Are you Sure?',
      text: `Once submitted, you can't revert it`,
      allowOutsideClick: false,
      showCancelButton: true,
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
    if (result.isConfirmed) {
      try {
        setIsPlacingBid(true);
        const payload = {
          auction_id: auction?.id,
          bidder_id: user?.id,
          network_info: await getBrowserNetworkInfo(),
          amount: bidValue,
        };
        const response = (await placeBidAPi(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          if (response.data) {
            setAuction(response.data as Auction);
          }
          CustomSwal.fire({
            icon: 'success',
            title: 'Bid Place successfully',
            target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
          });
          fetchAuctionDetail();
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setIsPlacingBid(false);
    }
  };

  const applyToPlaceBid = useCallback(
    async (comment?: string) => {
      setIsPlacingBid(true);
      try {
        const payload = {
          auction_id: auction?.id,
          buyer_id: user?.id,
          buyer_comment: comment,
        };
        const response = (await createBidApplication(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          CustomSwal.fire({
            icon: 'success',
            title: 'Bid Request Sent',
            text: `Request has been sent and waiting for approval`,
            target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
          });
          fetchAuctionDetail();
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setIsPlacingBid(false);
    },
    [dispatch, user, auction, fetchAuctionDetail]
  );

  const sendAutoBidRequest = useCallback(async () => {
    try {
      const auto_bid_detail = auction?.auto_bid_detail || null;
      setIsPlacingBid(true);
      const payload = {
        bidder_id: user?.id,
        increment: autoBidIncrementValue,
        max_amount: maxAutoBidAmount,
        network_info: await getBrowserNetworkInfo(),
      };
      let response: any = {};
      if (auto_bid_detail) {
        response = (await updateAutoBidRequestApi({
          ...payload,
          id: auto_bid_detail?.id,
        })) as APIResponse<FreeKeyObject>;
      } else {
        response = (await createAutoBidRequestAPI({
          ...payload,
          auction_id: auction?.id,
        })) as APIResponse<FreeKeyObject>;
      }
      if (response.status) {
        fetchAuctionDetail();
        // setAutoBidIncrementValue('');
        // setMaxAutoBidAmount('');
        CustomSwal.fire({
          icon: 'success',
          title: 'Auto bid created sucessfully',
          target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
        });
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
    setIsPlacingBid(false);
  }, [
    dispatch,
    auction?.id,
    user?.id,
    auction?.auto_bid_detail,
    maxAutoBidAmount,
    autoBidIncrementValue,
    fetchAuctionDetail,
  ]);

  const openBidRequestForm = useCallback(async () => {
    const result = await CustomSwal.fire({
      title: 'Apply/Request for Bid',
      text: 'Once submitted, your request will go for approval',
      input: 'textarea',
      inputPlaceholder: 'Optional. Type your message here...',
      inputAttributes: {
        'aria-label': 'Type your message here',
        maxlength: '100',
      },
      allowOutsideClick: false,
      showCancelButton: true,
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
    if (result.isConfirmed) applyToPlaceBid(result.value);
    else setIsPlacingBid(false);
  }, [applyToPlaceBid]);

  const removeAutoBid = useCallback(async () => {
    const result = await CustomSwal.fire({
      title: 'Are you Sure?',
      text: `Once done, you can't revert it`,
      allowOutsideClick: false,
      showCancelButton: true,
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
    if (result.isConfirmed) {
      try {
        setIsAutoBidRemoving(true);
        const auto_bid_detail = auction?.auto_bid_detail || null;
        const payload = {
          id: auto_bid_detail?.id,
          bidder_id: user?.id,
        };
        const response = (await deleteAutoBidRequestApi(payload)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          fetchAuctionDetail();
          CustomSwal.fire({
            icon: 'success',
            title: 'Auto bid cancelled',
            target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
          });
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setIsAutoBidRemoving(false);
    }
  }, [dispatch, user?.id, auction?.auto_bid_detail, fetchAuctionDetail]);

  useEffect(() => {
    let isValid = true;
    if (parseIntoNumber(autoBidIncrementValue) < parseIntoNumber(auction?.increment_price)) {
      isValid = false;
    } else if (parseIntoNumber(maxAutoBidAmount) < getNextBidAmount(auction)) {
      isValid = false;
    }
    setDisableAutoBidSubmit(!isValid);
  }, [auction, maxAutoBidAmount, autoBidIncrementValue]);

  useEffect(() => {
    fetchAuctionDetail();
  }, [fetchAuctionDetail]);

  const renderBidPlaceForm = () => {
    const nextBidAmount = getNextBidAmount(auction as Auction);
    const auto_bid_detail = auction?.auto_bid_detail || null;
    return isAuctionLive(auction?.start_time || null, auction?.end_time || null) ? (
      <>
        <Box sx={{mb: 4}}>
          <HorizontalTabs
            items={[
              { label: 'Standard Bid', value: 0, icon: <GavelIcon sx={{fontSize: '14px'}} /> },
              { label: 'Auto Bid', value: 1, icon: <ElectricBoltSharpIcon sx={{fontSize: '14px'}} /> }
            ]}
            selectedTab={tab}
            onChange={(newValue: string | number) => setTab(newValue)}
            size="small"
            shape="rounded"
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          {tab === 0 && (
            <>
              <TextField
                fullWidth
                label={`Bid Price (${getFormattedEmptyAmountWithUnit(auction?.quantity_unit)})`}
                placeholder={`Minimum ${getFormattedPrice(nextBidAmount)}`}
                value={bidValue}
                onChange={(e) => {
                  setBidValue(e.target.value);
                  if (parseIntoNumber(e.target.value) < nextBidAmount) {
                    setDisablePlaceBidBtn(true);
                  } else {
                    setDisablePlaceBidBtn(false);
                  }
                }}
                size="small"
                type="number"
                slotProps={{
                  htmlInput: {
                    min: nextBidAmount,
                    step: parseIntoNumber(auction?.increment_price),
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handlePlaceBid}
                sx={{ px: 4, whiteSpace: 'nowrap', backgroundColor: '#128d6a' }}
                disabled={!bidValue || isPlacingBid || disablePlaceBidBtn}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <GavelIcon sx={{fontSize: '14px'}} />
                  Place Bid
                </Stack>
              </Button>
            </>
          )}
          {tab === 1 && (
            <>
              <TextField
                fullWidth
                label="Increment Amount"
                placeholder={`Minimum ${getFormattedPrice(auction?.increment_price)}`}
                value={autoBidIncrementValue}
                onChange={(e) => {
                  setAutoBidIncrementValue(e.target.value);
                }}
                size="small"
                type="number"
                slotProps={{
                  htmlInput: {
                    min: parseIntoNumber(auction?.increment_price),
                    step: parseIntoNumber(auction?.increment_price),
                  },
                }}
              />

              <TextField
                fullWidth
                label="Max. Amount"
                placeholder="Enter your maximum bid amount"
                value={maxAutoBidAmount}
                onChange={(e) => {
                  setMaxAutoBidAmount(e.target.value);
                }}
                size="small"
                type="number"
                slotProps={{
                  htmlInput: {
                    min: nextBidAmount,
                    step: parseIntoNumber(auction?.increment_price),
                  },
                }}
              />
              <Button
                variant="contained"
                // color={auto_bid_detail ? 'error' : 'primary'}
                onClick={() => auto_bid_detail ? removeAutoBid() : sendAutoBidRequest()}
                sx={{
                  px: 4,
                  whiteSpace: 'nowrap',
                  backgroundColor: auto_bid_detail ? '#ff1f5a' : '#155dfc'
                }}
                disabled={disableAutoBidSubmit || isAutoBidRemoving || isPlacingBid}
                title={auto_bid_detail ? 'Click to cancel auto bid' : 'Enable Auto Bid'}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <ElectricBoltSharpIcon sx={{fontSize: '14px'}} />
                  {auto_bid_detail ? 'Cancel Auto Bid' : 'Enable Auto Bid'}
                </Stack>
              </Button>
            </>
          )}
        </Stack>
        {(tab === 0 && disablePlaceBidBtn) && (
          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
            {`Bid value must be greater than or equal to ${getFormattedPrice(nextBidAmount)}`}
          </Typography>
        )}
        <Typography variant="caption" color="default" sx={{ display: 'block', mt: 1 }}>
          Minimum increment: {getFormattedPrice(auction?.increment_price)}{' '}
          {getFormattedEmptyAmountWithUnit(auction?.quantity_unit)}
        </Typography>
        {tab === 1 && (
          <Typography variant="caption" color="default" sx={{ display: 'block' }}>
            Maximum amount for auto bid must be greater or equal than next bid amount.
          </Typography>
        )}
      </>
    ) : (
      <CustomAlert type="warning">Auction has not started yet</CustomAlert>
    );
  };

  const renderBidApplyForm = () => {
    return (
      <Stack direction="column" spacing={2}>
        <CustomAlert type="warning">You must need to apply/request before placing bid</CustomAlert>
        <Button
          variant="contained"
          color="secondary"
          onClick={openBidRequestForm}
          sx={{ px: 4, whiteSpace: 'nowrap' }}
        >
          Request/Apply For Bid
        </Button>
      </Stack>
    );
  };

  const renderBidRequestStatus = () => {
    return (
      <Stack direction="column" spacing={2}>
        <CustomAlert type="info">
          Your request to place bid on this auction is waiting for approval
        </CustomAlert>
      </Stack>
    );
  };

  const startAuctionEndTime = useCallback(() => {
    fetchAuctionDetail();
    CustomSwal.fire({
      icon: 'info',
      title: 'Auction is now started. You can place your bid now.',
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
  }, [fetchAuctionDetail]);

  const closeAuction = useCallback(() => {
    setShowAuctionCloseMessage(true);
    CustomSwal.fire({
      icon: 'info',
      title: 'Auction has been closed',
      target: document.querySelector('.MuiDialog-container') as HTMLElement | null,
    });
  }, []);

  useAuctionWsHook(auction_id, token, fetchAuctionDetail);

  const isComingAuction = isAuctionFuture(auction?.start_time as string);

  if (showAuctionCloseMessage) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6fbf9' }}>
        <CustomAlert type="info">
          <Typography variant="h4" fontWeight="600">Auction has been closed. Thank you for participating in the auction.</Typography>
          <Typography variant="body1" mt={2}>Winner will be announced soon by seller.</Typography>
        </CustomAlert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6fbf9', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: '#155dfc' }} variant="rounded">
              <GavelIcon sx={{fontSize: '18px', color: '#fff'}} />
            </Avatar>
            <Box>
              <Typography component="p" sx={{ textTransform: 'uppercase' }}>{auction?.title}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 300, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarMonthOutlinedIcon color="secondary" sx={{fontSize: '12px' }} />
                {getFormattedDate(auction?.created_at as string)}
              </Typography>
            </Box>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {auction?.description}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid size={{xs: 12, md: 8}}>
            <Card elevation={2} sx={{borderRadius: 3}}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background: "linear-gradient(135deg,#0ea472,#128d6a)",
                  color: "white",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  height: '165px'
                }}
              >
                <Stack direction={{xs: 'column', md: 'row'}} gap={2}>
                  <Stack sx={{ justifyContent: 'center', alignItems: 'center', gap: 1, flex: {xs: 1, md: 6}}}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1}}>
                      <TrendingUpIcon sx={{ fontSize: 20, color: '#FFFFFF' }}  />
                      <Typography variant="body2" fontSize="14px" color="#FFFFFF" textTransform="uppercase">
                        {isComingAuction ? 'Starting Price' : 'Current Highest Bid'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1}}>
                      <Typography variant="h2" fontWeight="600" color="#FFFFFF">
                        {!isComingAuction
                          ? getFormattedPrice(isComingAuction ? auction?.starting_price : auction?.current_price)
                          : 'XXXXXX'
                        }
                      </Typography>
                      <Typography variant="body2" color="#FFFFFF">
                        {getFormattedEmptyAmountWithUnit(isComingAuction ? auction?.quantity_unit : auction?.quantity_unit)}
                      </Typography>
                    </Stack>
                    <Chip label={isComingAuction ? 'Upcoming Auction' : 'Live Auction'} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }} />
                  </Stack>
                  <Box sx={{flex: {xs: 1, md: 4}, display: {xs: 'block', md: 'none'}}}>
                    <Card sx={{ p: 4, borderRadius: 3, bgcolor: "#ff1f5a", color: "white", textAlign: "center" }}>
                      <Typography variant="body1" sx={{ mb: 2, textTransform: 'uppercase', color: '#FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimerOutlinedIcon fontSize="small" /> {isComingAuction ? `Auction will start in` : `Time Remaining`}
                      </Typography>
                      <CountdownWidget
                        endTime={(isComingAuction ? auction?.start_time : auction?.end_time) as string}
                        onEnd={() => isComingAuction ? startAuctionEndTime() : closeAuction()}
                        showZeroDays={isComingAuction}
                      />
                    </Card>
                  </Box>
                </Stack>
              </Box>
              {allowPlaceBid && (
                <Box sx={{ mb: 4, p: 4 }}>
                  {isComingAuction 
                    ? (!auction?.bid_apply_detail || auction?.bid_apply_detail?.status === 'reject') && (
                      <>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                          Apply For Bid
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Apply for bid to participate in the auction
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 300, mb: 1 }}>
                          <Typography component="span">₹ </Typography>
                          Place Your Bid
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>Choose your bidding strategy below</Typography>
                      </>
                    )}
                  {isAuthenticated ? (
                    auction?.bid_apply_detail ? (
                      auction?.bid_apply_detail.status === 'pending' ? (
                        renderBidRequestStatus()
                      ) : auction?.bid_apply_detail.status === 'reject' ? (
                        renderBidApplyForm()
                      ) : (
                        isComingAuction
                          ? <CustomAlert type="info">
                            Please wait for auction to start
                          </CustomAlert>
                          : renderBidPlaceForm()
                      )
                    ) : (
                      renderBidApplyForm()
                    )
                  ) : (
                    <Box alignItems="center">
                      <Button
                        variant="contained"
                        onClick={() => setShowAuthFormModal(true)}
                        sx={{ px: 4, whiteSpace: 'nowrap' }}
                      >
                        Login to Place Bid
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
              {viewType === 'seller' && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h3" sx={{ mb: 2 }}>Bids</Typography>
                  <AuctionBidGridWidget auction_id={parseIntoNumber(auction_id)} viewType="seller" />
                </Box>
              )}
            </Card>
            <Card sx={{ p: 4, mt: 3, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 2, borderLeft: '5px solid #155dfc', pl: 2 }}>Bidding Guidelines</Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'start', gap: 1}}>
                  <Avatar sx={{ bgcolor:  '#dbeafe', color: '#155dfc', fontSize: '9px', width: '20px', height: '20px' }}>1</Avatar>
                  {!isComingAuction
                    ? `Each bid must be at least ${getFormattedPrice(auction?.increment_price)} ${getFormattedEmptyAmountWithUnit(auction?.quantity_unit)} higher than the current bid`
                    : `Each bid will must be greater then the current bid`
                  }
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'start', gap: 1}}>
                  <Avatar sx={{ bgcolor:  '#dbeafe', color: '#155dfc', fontSize: '9px', width: '20px', height: '20px' }}>2</Avatar>
                  Auto-bidding will automatically place bids on your behalf up to your maximum
                  price
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'start', gap: 1}}>
                  <Avatar sx={{ bgcolor:  '#dbeafe', color: '#155dfc', fontSize: '9px', width: '20px', height: '20px' }}>3</Avatar>
                  The auction will close at the specified end time or when no new bids are
                  placed
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'start', gap: 1}}>
                  <Avatar sx={{ bgcolor:  '#dbeafe', color: '#155dfc', fontSize: '9px', width: '20px', height: '20px' }}>4</Avatar>
                  You can cancel auto-bidding at any time before the auction ends
                </Typography>
              </Stack>
            </Card>
          </Grid>
          <Grid size={{xs: 12, md: 4}}>
            <Card sx={{ mb: 3, display: {xs: 'none', md: 'block'}, p: 4, borderRadius: 3, bgcolor: "#ff1f5a", color: "white"}}>
              <Typography variant="body1" sx={{ mb: 2, textTransform: 'uppercase', color: '#FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerOutlinedIcon fontSize="small" /> {isComingAuction ? `Auction will start in` : `Time Remaining`}
              </Typography>
              <CountdownWidget
                endTime={(isComingAuction ? auction?.start_time : auction?.end_time) as string}
                onEnd={() => isComingAuction ? startAuctionEndTime() : closeAuction()}
                showZeroDays={isComingAuction}
              />
            </Card>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: '#155dfc', width: '20px', height: '20px' }} variant="rounded" >
                    <ReceiptLongOutlinedIcon sx={{fontSize: '14px', color: '#fff'}} />
                  </Avatar>
                  <Typography fontWeight={700}>Company Details</Typography>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                <Typography variant="caption" color="text.secondary">
                  Company Name
                </Typography>
                <Typography sx={{ mb: 1, fontWeight: 600 }}>
                  {user?.company_name ? user?.company_name : 'Not Provided'}
                </Typography>

                <Divider sx={{ mb: 1, borderColor: '#00000012' }} />

                <Typography variant="caption" color="text.secondary">
                  Company Address
                </Typography>
                <Typography sx={{ mb: 1 }}>{user?.address}</Typography>
              </CardContent>
            </Card>

            {/* Bidding details card */}
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: '#9810fa', width: '20px', height: '20px' }} variant="rounded">
                    <AccessTimeOutlinedIcon sx={{fontSize: '14px', color: '#fff'}} />
                  </Avatar>
                  <Typography fontWeight={700}>Bidding Details</Typography>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                <Typography variant="caption" color="text.secondary">
                  Start Time
                </Typography>
                <Typography sx={{ mb: 1 }}>{getFormattedDate(auction?.start_time)}</Typography>
                <Divider sx={{ mb: 1, borderColor: '#00000012' }} />
                <Typography variant="caption" color="text.secondary">
                  End Time
                </Typography>
                <Typography sx={{ mb: 1 }}>{getFormattedDate(auction?.end_time)}</Typography>
                {!isComingAuction && (
                  <>
                    <Divider sx={{ mb: 1, borderColor: '#00000012' }} />
                    <Typography variant="caption" color="text.secondary">
                      Bid Increment
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      {getFormattedPrice(auction?.increment_price)}{' '}
                      {getFormattedEmptyAmountWithUnit(auction?.quantity_unit)}
                    </Typography>  
                  </>
                )}
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 2, background: '#fff7ed' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: '#f54a00', width: '20px', height: '20px' }} variant="rounded">
                    <ErrorOutlineOutlinedIcon sx={{fontSize: '14px', color: '#fff'}} />
                  </Avatar>
                  <Typography fontWeight={700}>Your Bid Status</Typography>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Next Bid
                    </Typography>
                    <Typography variant="body2">
                      {!isComingAuction
                        ? `${getNextBidAmount(auction as Auction)} ${getFormattedEmptyAmountWithUnit(auction?.quantity_unit)}`
                        : `--`
                      }
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1, borderColor: '#00000012' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      My Last Bid
                    </Typography>
                    <Typography variant="body2">
                      {getFormattedPrice(auction?.user_last_bid)}{' '}
                      {getFormattedEmptyAmountWithUnit(auction?.quantity_unit)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      {showAuthFormModal && (
        <AuthFormModal
          open={showAuthFormModal}
          onClose={() => setShowAuthFormModal(false)}
          onDone={() => setShowAuthFormModal(false)}
          size="xs"
        />
      )}
    </>
  );
};

export default BidWidget;
